import { isString, isNumber, toNumber } from 'lodash-es';
import { b2cVersion } from '../consts.ts';
import { isBigInt } from '../helpers.ts';
import { multipliersRatios } from '../racing-page/consts.ts';
import {
  stablesData,
  normalTokensIcons,
  HorseName,
  AGroupTokens,
  BGroupTokens,
} from './consts.ts';
import { horseNames, stablesNames, type StableName } from './horses.ts';

const isValidTokenName = (name: unknown): name is HorseName =>
  isString(name) && horseNames.includes(name as HorseName);

const isValidStableName = (name: unknown): name is StableName =>
  isString(name) && stablesNames.includes(name as StableName);

export const isValidTokenOrGroupName = (
  name: unknown
): name is HorseName | StableName =>
  isValidTokenName(name) || isValidStableName(name);

export const isStableBetName = isValidStableName;

export const getMultiplierRatioByPlace = (index: number) => {
  const value = multipliersRatios[index];
  if (!value) {
    throw new Error(`Multiplier ratio for place ${index} not found`);
  }
  return multipliersRatios[index];
};

type CalcMultiplierConfig = {
  multiplierRatioPlace?: number | null;
  feePercentage?: number; // Fee percentage to deduct from the sum (e.g., 5 for 5%)
};

const defCalcMultiplierConfig: Required<CalcMultiplierConfig> = {
  multiplierRatioPlace: 0,
  feePercentage: 5, // Default 5% fee
};

/**
 * Calculates a multiplier based on the provided sum and mult values, with optional configuration options.
 *
 * @param {unknown} sum - The value to be used as the numerator in the calculation. Must be a number or bigint for valid calculation.
 * @param {unknown} mult - The value to be used as the denominator in the calculation. Must be a number or bigint and not equal to zero.
 * @param {CalcMultiplierConfig} [config] - Optional configuration object to customize calculation behavior.
 * @param {number|null} [config.multiplierRatioPlace=0] - Index for multipliersRatios array to apply additional coefficient.
 *   If specified, multiplies result by multipliersRatios[multiplierRatioPlace]. If null, no additional coefficient applied.
 * @param {number} [config.feePercentage=5] - Fee percentage to deduct from the sum before calculation (e.g., 5 for 5%).
 */
export const calcMultiplier = (
  sum: unknown,
  mult: unknown,
  config?: CalcMultiplierConfig
): number => {
  config = {
    ...defCalcMultiplierConfig,
    ...config,
  };

  const isSumValid = isNumber(sum) || isBigInt(sum);
  const isMultValid = isNumber(mult) || isBigInt(mult);

  if (!(isSumValid && isMultValid)) {
    return 0;
  }

  const numSum = isBigInt(sum) ? Number(sum) : (sum as number);
  const numMult = isBigInt(mult) ? Number(mult) : (mult as number);

  if (numMult === 0) {
    return 0;
  }

  // Apply fee reduction to the sum
  const feeReduction = 1 - (config.feePercentage ?? 5) / 100;
  const adjustedSum = numSum * feeReduction;

  return (
    (adjustedSum / numMult) *
    (isNumber(config.multiplierRatioPlace)
      ? getMultiplierRatioByPlace(config.multiplierRatioPlace)
      : 1)
  );
};

export const getMultiplierValue = (value: unknown) => {
  if (!isNumber(value) || value === 0) return '';
  return value.toFixed(2) + 'x';
};

export const convertCentsToUnits = (value: unknown) => {
  if (isString(value)) {
    const numValue = toNumber(value);
    if (isNaN(numValue)) {
      return '0';
    }
    value = numValue;
  }
  // b2c version doesn't need to convert to units, only at b2b.
  if (b2cVersion) {
    return isNumber(value) ? value : '0';
  }

  return isNumber(value)
    ? new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value / 100)
    : '0';
};

export const getTokenIcon = (name: HorseName | StableName): string => {
  if (isStableBetName(name)) {
    return stablesData[name].icon;
  }
  return normalTokensIcons[name];
};

export const getTokenGroup = (token: unknown): StableName => {
  if (!isValidTokenName(token)) {
    throw new Error('Invalid token in getTokenGroup');
  }
  switch (true) {
    case AGroupTokens.includes(token):
      return 'A';
    case BGroupTokens.includes(token):
      return 'B';
    default:
      throw new Error(`Token ${token} does not belong to any group`);
  }
};
